import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { BuilderResultsTable } from './BuilderResultsTable';
import { LegacyReportResultsTable } from './LegacyReportResultsTable';
import type { ReportBuilderResult } from './hooks/useReportBuilder';
import type { ReportResult } from './hooks/useReports';

export type ReportResultsModalPayload =
  | { kind: 'builder'; result: ReportBuilderResult }
  | { kind: 'legacy'; result: ReportResult; page: number };

interface ReportResultsModalProps {
  open: boolean;
  title: string;
  loading?: boolean;
  error?: string | null;
  payload?: ReportResultsModalPayload | null;
  canExport?: boolean;
  canEdit?: boolean;
  onClose: () => void;
  onExport?: () => void;
  onEdit?: () => void;
  onLegacyPageChange?: (page: number) => void;
}

export function ReportResultsModal({
  open,
  title,
  loading = false,
  error = null,
  payload = null,
  canExport = false,
  canEdit = false,
  onClose,
  onExport,
  onEdit,
  onLegacyPageChange,
}: ReportResultsModalProps) {
  return (
    <Modal open={open} onClose={onClose} title={title} size="2xl" closeOnOverlayClick={!loading}>
      <div className="min-h-[12rem]">
        {loading && (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600" />
            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Running report…</p>
          </div>
        )}

        {!loading && error && (
          <p className="py-8 text-center text-sm text-red-600 dark:text-red-400">{error}</p>
        )}

        {!loading && !error && payload?.kind === 'builder' && (
          <BuilderResultsTable result={payload.result} compact />
        )}

        {!loading && !error && payload?.kind === 'legacy' && onLegacyPageChange && (
          <LegacyReportResultsTable
            result={payload.result}
            page={payload.page}
            onPageChange={onLegacyPageChange}
            embedded
          />
        )}
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-end gap-2 border-t border-border pt-4 dark:border-border-dark">
        {canEdit && onEdit && (
          <Button variant="outline" size="sm" onClick={onEdit} disabled={loading}>
            Edit
          </Button>
        )}
        {canExport && onExport && payload?.kind === 'builder' && (
          <Button variant="outline" size="sm" onClick={onExport} disabled={loading || !!error}>
            Export Excel
          </Button>
        )}
        <Button size="sm" onClick={onClose} disabled={loading}>
          Close
        </Button>
      </div>
    </Modal>
  );
}
